import { useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';

import * as styles from './StickyHeader.css';

interface StickyHeaderProps {
  navigation: ReactNode;
  breadcrumbs?: ReactNode;
}

/**
 * Sticky header component that keeps navigation and breadcrumbs
 * visible at the top of the viewport while scrolling
 *
 * Features:
 * - Sticky positioning for always-visible navigation
 * - Shadow effect when scrolled for visual depth
 * - Combines navigation and breadcrumbs in single sticky container
 * - Optimized scroll listener with passive event handling
 */
export function StickyHeader({ navigation, breadcrumbs }: StickyHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      // Add shadow when scrolled past header
      setIsScrolled(window.scrollY > 0);
    };

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={headerRef} className={styles.stickyHeaderWrapper} data-scrolled={isScrolled}>
      <div className={styles.navigationBarWrapperSticky}>{navigation}</div>
      {breadcrumbs && <div className={styles.breadcrumbsWrapperSticky}>{breadcrumbs}</div>}
    </div>
  );
}
